import { fetchWithCSRF } from "@/helpers/common/csrf";
import { ArrowLeft } from "lucide-react";
import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { formatDate, formatSeconds, shortName, StatusBadge } from "./_helpers";
import { BackgroundTaskDetail } from "./_types";

const API_URL = import.meta.env.VITE_VANDERHEIM_NINJA_API_URL;

function Block({ title, text }: { title: string; text: string }) {
    if (!text) return null;
    return (
        <section className="mt-4">
            <h2 className="mb-1 text-sm font-semibold text-slate-900 dark:text-slate-100">
                {title}
            </h2>
            <pre className="overflow-x-auto rounded-lg bg-slate-100 p-3 text-xs break-words whitespace-pre-wrap text-slate-800 dark:bg-slate-900 dark:text-slate-200">
                {text}
            </pre>
        </section>
    );
}

export default function BackgroundTaskDetailPage() {
    const { taskId } = useParams();
    const [task, setTask] = useState<BackgroundTaskDetail | null>(null);
    const [failed, setFailed] = useState(false);

    useEffect(() => {
        const controller = new AbortController();
        fetchWithCSRF(
            `${API_URL}/longhouse-api/village/background-tasks/${taskId}`,
            { signal: controller.signal },
        )
            .then(async (res) => {
                if (!res.ok) throw new Error(`Status ${res.status}`);
                setTask(await res.json());
            })
            .catch((error) => {
                if (controller.signal.aborted) return;
                console.error("Error fetching background task:", error);
                setFailed(true);
            });
        return () => controller.abort();
    }, [taskId]);

    const details = task && [
        ["Queue", task.queue || "—"],
        ["Worker", task.worker || "—"],
        ["Retries", String(task.retries)],
        ["Waited", formatSeconds(task.wait_seconds)],
        ["Ran for", formatSeconds(task.duration_seconds)],
        ["Queued", formatDate(task.queued_at)],
        ["Started", formatDate(task.started_at)],
        ["Finished", formatDate(task.finished_at)],
        ["Task ID", task.task_id],
    ];

    return (
        <div className="px-4 py-6 sm:px-6 lg:px-8">
            <Link
                to="/longhouse/background-tasks"
                className="mb-4 inline-flex items-center gap-1 text-sm text-slate-600 hover:underline dark:text-slate-400"
            >
                <ArrowLeft className="h-4 w-4" />
                Background Tasks
            </Link>

            {failed ? (
                <p className="p-8 text-center text-sm text-red-600 dark:text-red-400">
                    This task could not be loaded.
                </p>
            ) : !task ? (
                <p className="p-8 text-center text-sm text-slate-600 dark:text-slate-400">
                    Loading…
                </p>
            ) : (
                <>
                    <div className="mb-4 flex flex-wrap items-center gap-3">
                        <h1 className="text-2xl font-bold break-all text-slate-900 dark:text-slate-100">
                            {shortName(task.name)}
                        </h1>
                        <StatusBadge status={task.status} />
                    </div>
                    <p className="mb-4 text-sm break-all text-slate-600 dark:text-slate-400">
                        {task.name}
                    </p>
                    <dl className="grid gap-3 rounded-xl border border-slate-200 bg-white p-4 sm:grid-cols-2 lg:grid-cols-3 dark:border-slate-700 dark:bg-slate-800">
                        {details?.map(([label, value]) => (
                            <div key={label}>
                                <dt className="text-xs text-slate-600 dark:text-slate-400">
                                    {label}
                                </dt>
                                <dd className="text-sm break-all text-slate-900 dark:text-slate-100">
                                    {value}
                                </dd>
                            </div>
                        ))}
                    </dl>
                    <Block title="Arguments" text={task.arguments} />
                    <Block title="Result" text={task.result} />
                    <Block title="Error" text={task.error} />
                    <Block title="Traceback" text={task.traceback} />
                </>
            )}
        </div>
    );
}
